<template>
  <section class="home-menu" id="menu">
    <div class="container">
      <div class="section-header text-center" data-aos="fade-up" data-aos-duration="800">
        <span class="section-tag">
          <i class="ri-restaurant-2-line"></i> Our Menu
        </span>
        <h2>Packed With <span class="text-accent">Flavor</span></h2>
        <p class="section-desc">
          From birria tacos to loaded bowls — every dish is crafted with 
          100% Zabiha halal meats and fresh, high-quality ingredients.
        </p>
      </div>

      <div class="menu-categories" data-aos="fade-up" data-aos-duration="800" data-aos-delay="200">
        <button
          v-for="cat in categories"
          :key="cat.id"
          :class="['cat-btn', { active: activeCategory === cat.id }]"
          @click="activeCategory = cat.id"
        >
          {{ cat.label }}
        </button>
      </div>

      <div class="menu-grid">
        <div
          v-for="(item, index) in filteredItems"
          :key="item.name"
          class="menu-card"
          data-aos="fade-up"
          data-aos-duration="800"
          :data-aos-delay="100 * index"
        >
          <div class="card-image">
            <img :src="item.image" :alt="item.name" />
            <div class="card-badge" v-if="item.badge">{{ item.badge }}</div>
          </div>
          <div class="card-body">
            <div class="card-header-row">
              <h3>{{ item.name }}</h3>
              <span class="card-price">{{ item.price }}</span>
            </div>
            <p>{{ item.description }}</p>
            <div class="card-tags">
              <span v-for="tag in item.tags" :key="tag" class="tag">{{ tag }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";

interface MenuItem {
  name: string;
  price: string;
  description: string;
  image: string;
  category: string;
  badge?: string;
  tags: string[];
}

export default defineComponent({
  name: "HomeMenu",
  setup() {
    const activeCategory = ref("all");

    const categories = [
      { id: "all", label: "All" },
      { id: "tacos", label: "Tacos" },
      { id: "bowls", label: "Bowls" },
      { id: "quesadillas", label: "Quesadillas" },
      { id: "sides", label: "Sides & More" },
    ];

    const menuItems: MenuItem[] = [
      {
        name: "Birria Tacos",
        price: "$12.99",
        description: "Slow-cooked halal beef birria with melted cheese, onions, cilantro, and consommé for dipping.",
        image: "https://images.unsplash.com/photo-1599974579688-8dbdd335c77f?auto=format&fit=crop&w=500&q=80",
        category: "tacos",
        badge: "Best Seller",
        tags: ["Halal Beef", "Spicy"],
      },
      {
        name: "Chicken Tinga Tacos",
        price: "$10.99",
        description: "Shredded halal chicken in smoky chipotle sauce with avocado crema and cotija cheese.",
        image: "https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?auto=format&fit=crop&w=500&q=80",
        category: "tacos",
        tags: ["Halal Chicken", "Smoky"],
      },
      {
        name: "Burrito Bowl",
        price: "$13.99",
        description: "Your choice of protein with rice, beans, lettuce, pico de gallo, cheese, and sour cream.",
        image: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=500&q=80",
        category: "bowls",
        badge: "Popular",
        tags: ["Customizable", "Filling"],
      },
      {
        name: "Power Bowl",
        price: "$12.99",
        description: "Grilled halal chicken, quinoa, black beans, corn, bell peppers, and lime vinaigrette.",
        image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=500&q=80",
        category: "bowls",
        tags: ["Healthy", "Halal Chicken"],
      },
      {
        name: "Chicken Quesadilla",
        price: "$11.99",
        description: "Grilled halal chicken with Monterey Jack, bell peppers, and onions in a crispy flour tortilla.",
        image: "https://images.unsplash.com/photo-1618040996337-56904b7850b9?auto=format&fit=crop&w=500&q=80",
        category: "quesadillas",
        tags: ["Halal Chicken", "Cheesy"],
      },
      {
        name: "Fresh Guacamole",
        price: "$6.99",
        description: "Hand-mashed avocados with onions, cilantro, lime, and jalapeños — served with warm tortilla chips.",
        image: "https://images.unsplash.com/photo-1600891964092-4316c288032e?auto=format&fit=crop&w=500&q=80",
        category: "sides",
        badge: "Fan Favorite",
        tags: ["Vegan", "Fresh"],
      },
    ];

    const filteredItems = computed(() => {
      if (activeCategory.value === "all") return menuItems;
      return menuItems.filter(item => item.category === activeCategory.value);
    });

    return { activeCategory, categories, filteredItems };
  },
});
</script>

<style lang="scss" scoped>
.home-menu {
  padding: 120px 0;
  background: linear-gradient(180deg, #0f0f0f 0%, #141414 100%);

  .section-header {
    margin-bottom: 48px;

    .section-tag {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #b5ff00;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      margin-bottom: 16px;
    }

    h2 {
      font-size: 44px;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: -1px;
      margin-bottom: 16px;
      .text-accent {
        color: #b5ff00;
      }
    }

    .section-desc {
      font-size: 17px;
      color: rgba(255, 255, 255, 0.55);
      max-width: 540px;
      margin: 0 auto;
      line-height: 1.7;
    }
  }

  .menu-categories {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-bottom: 48px;
    flex-wrap: wrap;

    .cat-btn {
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 0.6);
      padding: 10px 24px;
      border-radius: 50px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        border-color: rgba(181, 255, 0, 0.3);
        color: #ffffff;
      }

      &.active {
        background: #b5ff00;
        border-color: #b5ff00;
        color: #0a0a0a;
      }
    }
  }

  .menu-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 28px;
    margin-bottom: 56px;
  }

  .menu-card {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    overflow: hidden;
    transition: all 0.4s ease;

    &:hover {
      border-color: rgba(181, 255, 0, 0.2);
      transform: translateY(-6px);
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);

      .card-image img {
        transform: scale(1.08);
      }
    }

    .card-image {
      position: relative;
      height: 220px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.6s ease;
      }

      .card-badge {
        position: absolute;
        top: 16px;
        left: 16px;
        background: #b5ff00;
        color: #0a0a0a;
        padding: 5px 14px;
        border-radius: 50px;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.3px;
      }
    }

    .card-body {
      padding: 24px;

      .card-header-row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 10px;
        gap: 12px;

        h3 {
          font-size: 18px;
          font-weight: 700;
          color: #ffffff;
          margin: 0;
        }

        .card-price {
          font-size: 18px;
          font-weight: 800;
          color: #b5ff00;
          white-space: nowrap;
        }
      }

      p {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.5);
        line-height: 1.6;
        margin-bottom: 16px;
      }

      .card-tags {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;

        .tag {
          background: rgba(181, 255, 0, 0.08);
          border: 1px solid rgba(181, 255, 0, 0.15);
          color: rgba(181, 255, 0, 0.8);
          padding: 4px 12px;
          border-radius: 50px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }
    }
  }

  .menu-cta {
    .btn-view-menu {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      background: transparent;
      border: 2px solid rgba(181, 255, 0, 0.4);
      color: #b5ff00;
      padding: 16px 40px;
      border-radius: 60px;
      font-size: 16px;
      font-weight: 700;
      text-decoration: none;
      transition: all 0.3s ease;

      &:hover {
        background: #b5ff00;
        color: #0a0a0a;
        border-color: #b5ff00;
        transform: translateY(-2px);
        box-shadow: 0 8px 30px rgba(181, 255, 0, 0.25);
      }
      i {
        transition: transform 0.3s ease;
      }
      &:hover i {
        transform: translateX(4px);
      }
    }
  }
}

@media (max-width: 991px) {
  .home-menu {
    padding: 80px 0;
    .section-header h2 {
      font-size: 34px;
    }
    .menu-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
    }
  }
}

@media (max-width: 575px) {
  .home-menu {
    .menu-grid {
      grid-template-columns: 1fr;
    }
    .menu-categories {
      gap: 8px;
      .cat-btn {
        padding: 8px 18px;
        font-size: 13px;
      }
    }
  }
}
</style>
